import { QuestNode, Act, ReplayModifier, loadPlayerArchetype } from './questline'
import { NewGameOptions, MAX_HANDICAP } from './engine'
import { Card, SECRET_RARITIES } from './types'
import { shuffle } from './engine/helpers'
import { HERO_CARDS, makeNodeDeck, getCardCatalog } from './cards'
import { analyseDeckPower, classifyDeckShape, DeckShape } from './deckPower'
import { loadPlayerStats } from './playerStats'
import { crystalTierMultiplier } from './economy'
import answerCardsData from '../data/answerCards.json'

const QB_ENVIRONMENTS = ['forest', 'farmland', 'ruins', 'ashen', 'sand', 'volcano', 'citadel', 'coast', 'frost', 'fungal', 'vault', 'camp'] as const
function pickQBEnvironment(): string {
  return QB_ENVIRONMENTS[Math.floor(Math.random() * QB_ENVIRONMENTS.length)]
}
import {
  buildDeckCards, STARTER_DECK, deckTotalCards,
  generateSeededPack, loadCollection, loadDeck,
} from './collection'

export type QuickBattleMode =
  | 'easy' | 'normal' | 'mirror' | 'unlimited' | 'chaos'
  | 'only-units' | 'only-spells' | 'only-buildings'
  | 'common-only' | 'uncommon-only' | 'rare-only' | 'legendary-only' | 'hero-only'
  | 'draft'

/**
 * Quick Battle's difficulty softener for a weak deck (#2296). Used to scale
 * with how far short of DECK_MAX the deck fell — which had the sign
 * backwards: per deckPower.ts's consistency bonus, a SMALLER deck draws its
 * best cards more reliably and is therefore stronger, so a short deck was
 * getting an easier opponent for being the stronger side. This keys on the
 * deck's actual power band instead: a Recruit-band deck gets real help, a
 * Mythic-band deck gets none — it doesn't need it.
 */
export function quickBattleDeckBonus(playerCards: Card[]): number {
  const tier = currentPlayerBandTier(playerCards)
  return Math.max(0, (5 - tier) * 3)
}

export function loadCurrentDeckInfo(): { playerCards: Card[]; deckBonus: number } {
  const collection  = loadCollection()
  const deckEntries = loadDeck()
  const deckCount   = deckTotalCards(deckEntries)
  const effectiveDeck = deckCount > 0 ? deckEntries : STARTER_DECK
  const playerCards   = buildDeckCards(effectiveDeck, collection)
  return { playerCards, deckBonus: quickBattleDeckBonus(playerCards) }
}

export function buildQuickBattleOpts(
  mode: QuickBattleMode,
  handicap: number,
): { opts: NewGameOptions; playerCards: Card[] } {
  const collection    = loadCollection()
  const deckEntries   = loadDeck()
  const deckCount     = deckTotalCards(deckEntries)
  const effectiveDeck = deckCount > 0 ? deckEntries : STARTER_DECK
  const playerCards   = buildDeckCards(effectiveDeck, collection)
  const deckBonus     = quickBattleDeckBonus(playerCards)
  const adjustedHandicap = Math.min(MAX_HANDICAP, handicap + deckBonus)

  const environment = pickQBEnvironment()

  if (mode === 'mirror') {
    return { playerCards, opts: { playerCards, opponentHandicap: MAX_HANDICAP, prebuiltOpponentDeck: shuffle([...playerCards]), environment } }
  }
  if (mode === 'easy') {
    return { playerCards, opts: { playerCards, opponentHandicap: adjustedHandicap, environment } }
  }
  if (mode === 'normal') {
    const ownedNames       = new Set(collection.filter(e => e.count > 0).map(e => e.cardName))
    const collectionPool   = getCardCatalog().filter(c => ownedNames.has(c.name))
    const opponentCardPool = collectionPool.length >= 20 ? collectionPool : undefined
    return { playerCards, opts: { playerCards, opponentHandicap: adjustedHandicap, opponentCardPool, environment } }
  }
  if (mode === 'chaos') {
    const collectionPool   = makeNodeDeck(generateSeededPack(20, 'legendary'))
    const opponentCardPool = collectionPool.length >= 20 ? collectionPool : undefined
    return { playerCards, opts: { playerCards: opponentCardPool, opponentHandicap: 0, opponentCardPool, forgiveManaLimit: true, environment } }
  }
  if (mode === 'unlimited') {
    const collectionPool   = getCardCatalog().filter(c => !SECRET_RARITIES.has(c.rarity))
    const opponentCardPool = collectionPool.length >= 20 ? collectionPool : undefined
    return { playerCards, opts: { playerCards, opponentHandicap: 0, opponentCardPool, environment } }
  }
  if (mode === 'hero-only') {
    return { playerCards, opts: { playerCards, opponentHandicap: 0, opponentCardPool: [...HERO_CARDS, ...HERO_CARDS, ...HERO_CARDS], forgiveManaLimit: true, environment } }
  }
  // Rarity / type filter modes
  const filterMap: Record<string, (c: Card) => boolean> = {
    'common-only':    c => c.rarity === 'common',
    'uncommon-only':  c => c.rarity === 'uncommon',
    'rare-only':      c => c.rarity === 'rare',
    'legendary-only': c => c.rarity === 'legendary',
    'only-buildings': c => c.cardType.includes('structure'),
    'only-units':     c => c.cardType.includes('unit'),
    'only-spells':    c => c.cardType.includes('upgrade'),
  }
  const filter = filterMap[mode]
  if (filter) {
    const collectionPool   = getCardCatalog().filter(c => filter(c) && !SECRET_RARITIES.has(c.rarity))
    const opponentCardPool = collectionPool.length >= 20 ? collectionPool : undefined
    return { playerCards, opts: { playerCards, opponentHandicap: 0, opponentCardPool, environment } }
  }
  // Fallback: easy
  return { playerCards, opts: { playerCards, opponentHandicap: adjustedHandicap, environment } }
}

export const HANDICAP_KEY = 'jarvs_handicap'

export function loadHandicap(): number {
  try {
    const v = localStorage.getItem(HANDICAP_KEY)
    if (v !== null) return Math.min(MAX_HANDICAP, Math.max(0, parseInt(v, 10)))
  } catch { /* ignore */ }
  return 0
}

// ─── Deck-power difficulty tier (#2290/#2291) ──────────────────────────────
//
// `node.handicap` (0-20) is authored across every act but `newGame()` never
// reads it once a node supplies `enemyDeckNames` and/or `opponentIntervalMs`
// — every authored battle/elite/boss node supplies both, so handicap reaches
// only a UI label. `enemyTier` (1-5) replaces it with something that
// actually resolves to battle effects, shifted by how the player's deck
// compares to what the act expects.

/** Un-migrated nodes (no `enemyTier`) fall back to this so they keep behaving as before. */
export function tierFromHandicap(handicap: number): number {
  return Math.max(1, Math.min(5, Math.round(handicap / 7) + 1))
}

export function nodeEnemyTier(node: QuestNode): number {
  return node.enemyTier ?? tierFromHandicap(node.handicap ?? 0)
}

/**
 * Pure numeric core, shared by the battle-options resolver below and by
 * NodePeekModal's "why is this harder/easier" display, so both read the
 * exact same delta.
 */
/** Exposed separately from effectiveTierFor so callers like the "overqualified" act-clear bonus (#2294) can key off it directly. */
export function deckDeltaFor(playerBandTier: number, expectedBand: number): number {
  return Math.max(-1, Math.min(2, playerBandTier - expectedBand))
}

export function effectiveTierFor(nodeTier: number, playerBandTier: number, expectedBand: number): number {
  return Math.max(1, Math.min(5, nodeTier + deckDeltaFor(playerBandTier, expectedBand)))
}

export function resolveEffectiveTier(node: QuestNode, act: Act | undefined, playerBandTier: number): number {
  return effectiveTierFor(nodeEnemyTier(node), playerBandTier, act?.expectedBand ?? 1)
}

/** The player's current deck-power band tier (1-5), for feeding into resolveEffectiveTier. */
export function currentPlayerBandTier(playerCards: Card[]): number {
  return analyseDeckPower(playerCards, {
    archetype: loadPlayerArchetype(),
    maxMana: loadPlayerStats().maxMana,
  }).band.tier
}

const TIER_NUMERAL = ['', 'I', 'II', 'III', 'IV', 'V']

/**
 * Reward-screen label for a tier-scaled reward — undefined at tier 2, since
 * that's the unscaled baseline and has nothing to explain. A silent bonus
 * (or penalty) teaches the player nothing; this is what makes the deal
 * ("harder fight, better reward") visible rather than a mystery multiplier.
 */
export function tierRewardLabel(effectiveTier: number): string | undefined {
  if (effectiveTier === 2) return undefined
  const mult = crystalTierMultiplier(effectiveTier).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
  return `TIER ${TIER_NUMERAL[effectiveTier]} · ×${mult} crystals`
}

interface TierEffect {
  /** Multiplies opponentBaseHp, after the existing enemyHpPercent modifier. */
  hpMultiplier: number
  /** Added to opponentIntervalMs — positive is slower/easier, negative faster/harder. */
  intervalDeltaMs: number
  /** Added to opponentStartCards, on top of the enemyHandBonus modifier total. */
  startCardsBonus: number
  /** Added to the opponent's mana floor (opponentAI.ts). */
  manaFloorBonus: number
  /** Raises the opponent's per-turn play ceiling (opponentAI.ts). Undefined = no change. */
  maxPlaysOverride?: number
}

/**
 * Tier 2 is the authored baseline — every field is a no-op there, which is
 * what makes "tier 2 produces byte-identical NewGameOptions to today"
 * possible to hold as a regression guard (see campaignHelpers.test.ts).
 */
const TIER_EFFECTS: Record<number, TierEffect> = {
  1: { hpMultiplier: 0.9, intervalDeltaMs:  600, startCardsBonus: 0, manaFloorBonus: 0 },
  2: { hpMultiplier: 1.0, intervalDeltaMs:    0, startCardsBonus: 0, manaFloorBonus: 0 },
  3: { hpMultiplier: 1.0, intervalDeltaMs: -400, startCardsBonus: 1, manaFloorBonus: 0 },
  4: { hpMultiplier: 1.0, intervalDeltaMs: -800, startCardsBonus: 2, manaFloorBonus: 1 },
  5: { hpMultiplier: 1.0, intervalDeltaMs: -1200, startCardsBonus: 2, manaFloorBonus: 2, maxPlaysOverride: 4 },
}

// ─── Counterplay: answer cards by tier (#2292) ─────────────
//
// A high-tier fight against a strong deck should meet something built to
// answer THAT deck, not a stat-inflated version of the same authored enemy.
// classifyDeckShape() reads what the player's plan actually is; the matching
// pool below is drawn from cards that already exist in the catalogue — see
// the pool comments in answerCards.json for why each one counters its shape.

const ANSWER_CARD_POOLS = answerCardsData as Record<DeckShape, string[]>

/** How many answer cards get appended, by effective tier. Tiers 1-2 get none — the authored deck stands as-is. */
const ANSWER_COUNT_BY_TIER: Record<number, number> = { 1: 0, 2: 0, 3: 1, 4: 2, 5: 3 }

/**
 * Deterministic, not random — enemyDeckNames decks are unshuffled by design
 * ("Preset node deck — deterministic and learnable", engine.ts), so a battle
 * a player retries should draw the exact same answer cards, not a fresh
 * shuffle each attempt.
 */
function stableIndex(seed: string, mod: number): number {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return mod > 0 ? h % mod : 0
}

/** Which answer cards a node should field, given the player's deck shape and the effective tier. */
export function answerCardsFor(shape: DeckShape, effectiveTier: number, seed: string): string[] {
  const count = ANSWER_COUNT_BY_TIER[effectiveTier] ?? 0
  const pool = ANSWER_CARD_POOLS[shape] ?? []
  if (count === 0 || pool.length === 0) return []
  const start = stableIndex(seed, pool.length)
  return Array.from({ length: Math.min(count, pool.length) }, (_, i) => pool[(start + i) % pool.length])
}

/**
 * Spreads extra names evenly through an already-authored (unshuffled) enemy
 * deck list, rather than appending them at the end — enemyDeckNames decks
 * draw in array order, so a card appended after a 15-20 card authored deck
 * would rarely be drawn before the battle ends.
 */
export function interleaveAnswerCards(base: string[], extras: string[]): string[] {
  if (extras.length === 0) return base
  const result = [...base]
  extras.forEach((name, i) => {
    const pos = Math.min(result.length, Math.round(((i + 1) * result.length) / (extras.length + 1)))
    result.splice(pos, 0, name)
  })
  return result
}

/**
 * Compute the NewGameOptions fields that depend on node data, act modifiers,
 * the player's deck-power tier relative to what the act expects (#2291), and
 * (bossSpawnKillPct only) the player's run count.
 *
 * `node.handicap` is passed straight through unreduced — `opponentHandicap`
 * is dead for every authored campaign node regardless (newGame() only reads
 * it when a node supplies neither `enemyDeckNames` nor `opponentIntervalMs`,
 * which every authored node does). The run-count-scaled reduction that used
 * to sit here punished nothing real and is gone (#2296) — `enemyTier`'s
 * deck-power delta is the actual difficulty lever now, and it responds to
 * the deck the player brought, not to how many times they've hit retry.
 *
 * `playerCards` should be the actual battle deck (post-mastery,
 * post-augments — what `buildDeckCards()` returns) so the tier resolution
 * reads the deck the player is really about to fight with. Defaults to `[]`
 * (→ band tier 1, no upward pressure) for callers that don't have it, such
 * as the balance harness's default sweep.
 */
export function resolvedNodeOpts(
  node: QuestNode,
  act: Act | undefined,
  runCount: number,
  modifiers: ReplayModifier[],
  playerCards: Card[] = [],
): Omit<NewGameOptions, 'playerCards'> {
  // Stack modifier values
  let hpPctBonus = 0
  let intervalReduction = 0
  let handBonus = 0
  for (const m of modifiers) {
    if (m.type === 'enemyHpPercent') hpPctBonus += m.value
    if (m.type === 'enemyIntervalReduction') intervalReduction += m.value
    if (m.type === 'enemyHandBonus') handBonus += m.value
  }

  const adjustedHandicap = node.handicap ?? 0
  // Boss default HP is 95; non-boss 82 (mirrors engine.ts defaults)
  const defaultHp = node.bossAI ? 95 : 82
  const baseHp = node.opponentBaseHp ?? defaultHp
  const modifierHp = Math.round(baseHp * (1 + hpPctBonus / 100))

  // When a modifier or tier reduces interval, fall back to 4000ms base if the node didn't specify one
  const effectiveTier = resolveEffectiveTier(node, act, currentPlayerBandTier(playerCards))
  const tier = TIER_EFFECTS[effectiveTier]
  const baseInterval = node.opponentIntervalMs ?? ((intervalReduction > 0 || tier.intervalDeltaMs !== 0) ? 4000 : undefined)
  const modifierInterval = baseInterval !== undefined ? baseInterval - intervalReduction : undefined

  const adjustedHp = Math.round(modifierHp * tier.hpMultiplier)
  const adjustedInterval = modifierInterval !== undefined
    ? Math.max(1000, modifierInterval + tier.intervalDeltaMs)
    : undefined

  // Boss shockwave kill pct: starts at 50%, increases by 10% per run, capped at 100%
  const bossSpawnKillPct = node.bossAI
    ? Math.min(1.0, 0.5 + (runCount - 1) * 0.1)
    : undefined

  // Append answer cards for the player's deck shape, spread through the
  // authored deck rather than only tacked on the end. Only nodes with an
  // authored enemyDeck get this — a node without one already draws from the
  // full handicap-filtered catalogue via generateBalancedOpponentDeck, which
  // has no fixed array to interleave into.
  const answers = node.enemyDeck?.length
    ? answerCardsFor(classifyDeckShape(playerCards), effectiveTier, node.id)
    : []
  const enemyDeckNames = answers.length > 0
    ? interleaveAnswerCards(node.enemyDeck!, answers)
    : node.enemyDeck

  return {
    opponentHandicap: adjustedHandicap,
    bossAI: node.bossAI,
    bossCard: node.bossCard,
    bossName: node.bossName,
    bossHpMultiplier: node.bossHpMultiplier,
    enemyDeckNames,
    terrainSeed: node.id,
    environment: node.environment ?? act?.environment,
    roads: node.roads ?? act?.roads,
    roadFollowing: node.roadFollowing ?? act?.roadFollowing ?? false,
    terrain: node.terrain ?? act?.terrain,
    decor: node.decor ?? act?.decor,
    terrainPaths: node.terrainPaths ?? act?.terrainPaths,
    terrainValidated: node.terrainValidated ?? act?.terrainValidated ?? false,
    opponentIntervalMs: adjustedInterval,
    opponentBaseHp: adjustedHp,
    opponentStartCards: handBonus + tier.startCardsBonus,
    opponentManaFloorBonus: tier.manaFloorBonus,
    opponentMaxPlaysOverride: tier.maxPlaysOverride,
    opponentTier: effectiveTier,
    bossSpawnKillPct,
  }
}

// ─── Campaign HP carry-forward ─────────────────────────────────────────────

/**
 * Computes the run's persisted current HP after a campaign battle win.
 *
 * `playerBase` (from the just-finished GameState) may be scaled up by an
 * equipped relic's flat HP bonus — relics.ts applies that bonus fresh to
 * both `hp` and `maxHp` at the start of every battle, and it is deliberately
 * excluded from `run.maxHp` (the relic is meant to be a transient per-battle
 * cushion, not a permanent stat). Carrying `playerBase.hp` straight into
 * `run.playerHp` would silently bank that bonus into the persisted pool
 * without `run.maxHp` ever growing to match, drifting the two apart over
 * repeated battles. Instead, measure damage taken against the (possibly
 * relic-inflated) battle pool and re-apply it to the relic-free run.maxHp,
 * so run.playerHp and run.maxHp always stay on the same scale.
 */
export function carryHpAfterBattle(runMaxHp: number, playerBase: { hp: number; maxHp: number }): number {
  const dmgTaken = playerBase.maxHp - playerBase.hp
  return Math.max(0, runMaxHp - dmgTaken)
}

/**
 * Applies a node-map event's `healHp` effect. Unlike the rest-node heal
 * choice, event healing always clamps to maxHp — events never grant an
 * overheal above the player's maximum.
 */
export function applyEventHeal(playerHp: number, maxHp: number, amount: number): number {
  return Math.min(maxHp, playerHp + amount)
}

/**
 * Applies a rest-node "heal" choice. Normally clamps to maxHp, but if the
 * player is already at full health, grants the heal as bonus HP above their
 * maximum instead of wasting it — this is an intentional overheal, not a bug.
 */
export function applyRestHeal(playerHp: number, maxHp: number, healAmount: number): { hp: number; message: string } {
  if (playerHp >= maxHp) {
    return {
      hp: playerHp + healAmount,
      message: `Already at full health — gained +${healAmount} bonus HP above your maximum!`,
    }
  }
  const gained = Math.min(healAmount, maxHp - playerHp)
  const hp = Math.min(playerHp + healAmount, maxHp)
  return { hp, message: `Healed ${gained} HP. (${playerHp} → ${hp})` }
}
